import React from 'react';
import {Box, Button, Flex, Tooltip, VStack} from '@chakra-ui/react';
import {IntegrationDetailsHeader} from './IntegrationDetailsHeader';
import {useIsCrmSyncing, useQueueFullCrmRun} from '../../hooks/api/crm';
import {useIntegrationDetailsState} from './IntegrationDetails.state';
import {useQueryClient} from '@tanstack/react-query';
import {useToast} from '@chakra-ui/react';
import {IntegrationKeys} from '../../hooks/api/integrations';
import {useCrmIntegrationFromContext} from './IntegrationDetailsContext';
import {formatRelative} from 'date-fns';
import {ManagedEnrichmentFieldMapping} from './ManagedEnrichmentFieldMapping';
import {AdditionalEnrichmentFieldMapping} from './AdditionalEnrichmentFieldMapping';
import {useIsFreeOrListBuilderCustomer} from '../../hooks/api/metadata';
import {Settings} from './SyncSettings';
import {CrmIntegrationActionMenu} from './CrmIntegrationActionMenu';
import {AllIntegrations} from './AllIntegrations';

export const CrmIntegrationDetails = () => {
  const integration = useCrmIntegrationFromContext();
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();

  const queueFullCrmRun = useQueueFullCrmRun();
  const toast = useToast();
  const queryClient = useQueryClient();

  const {serverState} = useIntegrationDetailsState();

  const isSyncInProgress = useIsCrmSyncing({
    onSyncComplete: (task) => {
      if (task.state === 'done') {
        toast({
          title: 'Sync complete',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        queryClient.invalidateQueries({
          queryKey: IntegrationKeys.get(integration.type),
        });
      }
    },
  });

  const onSyncClicked = () => {
    queueFullCrmRun.mutate(integration.type);

    toast({
      title: 'Sync started',
      status: 'info',
      duration: 5000,
      isClosable: true,
    });
  };

  const syncSettings = serverState?.config?.syncSettings;

  return (
    <Flex direction="column">
      <IntegrationDetailsHeader>
        <VStack>
          <Flex gap="2">
            {serverState?.status === 'active' ? (
              <Tooltip
                label="Please select a sync type"
                isDisabled={!!syncSettings}
              >
                <Button
                  colorScheme="kbuttonblue"
                  isDisabled={isSyncInProgress || !syncSettings}
                  isLoading={queueFullCrmRun.isPending}
                  fontWeight={400}
                  onClick={onSyncClicked}
                  px="8"
                >
                  Sync Now
                </Button>
              </Tooltip>
            ) : (
              <Button
                colorScheme="kbuttonblue"
                fontWeight={400}
                onClick={() =>
                  location.assign(AllIntegrations[integration.type].oauthUrl)
                }
                px="8"
              >
                Connect
              </Button>
            )}
            <CrmIntegrationActionMenu
              disconnectInfoMessage="This will disable syncing until the integration is re-authenticated."
              deleteWarningMessage="This will disable syncing and remove any enabled field mappings."
            />
          </Flex>
          <Box color={'kgray.300'} fontSize={'sm'} alignSelf="start">
            {isSyncInProgress
              ? 'Sync in progress...'
              : serverState?.lastEnrichment
                ? `Last sync: ${formatRelative(
                    serverState.lastEnrichment,
                    new Date()
                  )}`
                : ''}
          </Box>
        </VStack>
      </IntegrationDetailsHeader>

      <Flex direction="column" my={8} gap={4}>
        <Settings />
        <ManagedEnrichmentFieldMapping />
        {!isFreeOrListBuilderCustomer && <AdditionalEnrichmentFieldMapping />}
      </Flex>
    </Flex>
  );
};
